import React, { createContext, useState, useCallback, ReactNode } from "react";
import { authApi } from "../../api/api";
import { PaginationInfo } from "../../types/api";

interface Permission {
  id?: number;
  name: string;
  guard_name?: string;
}

interface PermissionsContextType {
  permissions: Permission[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchPermissions: (page?: number, perPage?: number) => Promise<void>;
  addPermission: (permission: Permission) => Promise<void>;
  editPermission: (id: number, permission: Permission) => Promise<void>;
  deletePermission: (id: number) => Promise<void>;
}

export const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchPermissions = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await authApi.get(`/permissions?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { permissions: [...], pagination: {...} } }
      // API may return permissions as array of strings or objects
      let permissionsArray: string[] | any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.permissions && Array.isArray(responseData.data.permissions)) {
          permissionsArray = responseData.data.permissions;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          permissionsArray = responseData.data;
          paginationData = responseData.pagination;
        } else if (responseData.permissions && Array.isArray(responseData.permissions)) {
          permissionsArray = responseData.permissions;
          paginationData = responseData.pagination;
        }
      } else if (responseData?.permissions && Array.isArray(responseData.permissions)) {
        permissionsArray = responseData.permissions;
      } else if (Array.isArray(responseData?.data)) {
        permissionsArray = responseData.data;
      }
      
      // Transform array of strings into array of objects with name, guard_name, and id
      const permissionsData: Permission[] = Array.isArray(permissionsArray)
        ? permissionsArray.map((permissionItem: string | any, index: number) => {
            if (typeof permissionItem === 'string') {
              return {
                id: index + 1, // Use index as temporary ID since API returns strings
                name: permissionItem,
                guard_name: "web", // Default guard name
              };
            } else {
              return {
                id: permissionItem.id || index + 1,
                name: permissionItem.name || permissionItem,
                guard_name: permissionItem.guard_name || "web",
              };
            }
          })
        : [];
      
      setPermissions(permissionsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setPermissions([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addPermission = async (permission: Permission): Promise<void> => {
    try {
      const response = await authApi.post("/permissions", {
        name: permission.name,
      });
      const newPermission = response.data?.data || response.data?.permission || response.data;
      setPermissions((prev) => [...prev, newPermission]);
    } catch (error) {
      console.error("Failed to add permission:", error);
      throw error;
    }
  };

  const editPermission = async (id: number, permission: Permission): Promise<void> => {
    try {
      const response = await authApi.put(`/permissions/${id}`, {
        name: permission.name,
      });
      const updatedPermission = response.data?.data || response.data?.permission || response.data;
      setPermissions((prev) =>
        prev.map((p) => (p.id === id ? updatedPermission : p))
      );
    } catch (error) {
      console.error("Failed to edit permission:", error);
      throw error;
    }
  };

  const deletePermission = async (id: number): Promise<void> => {
    try {
      await authApi.delete(`/permissions/${id}`);
      setPermissions((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error("Failed to delete permission:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchPermissions explicitly with pagination

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        pagination,
        loading,
        fetchPermissions,
        addPermission,
        editPermission,
        deletePermission,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsProvider;
